/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Checkbox,
  FormGroup,
  FormHelperText,
  Divider,
  Typography,
} from '@mui/material';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AuthRoles } from 'skipify-types';

import { Button, InfoTooltip } from '../../../components/core';
import { DialogCloseButton } from '../../../components/core/DialogCloseButton';
import {
  Auth0RoleSummary,
  CreatePortalUserBody,
  MerchantAccess,
  PortalUserDetail,
  UpdatePortalUserBody,
} from '../../../store/modules/merchant/types';
import { MerchantAccessMultiSelect } from './MerchantAccessMultiSelect';
import { getRoleRedundancyHelperText, normalizePortalRoleNames } from '../portalUserRoleRedundancy';
import { getPortalUserRoleLabel } from '../portalUserRoleCatalog';
import {
  PORTAL_USER_CHILD_MERCHANT_MERCHANT_ASSIGNMENT_HELPER,
  PORTAL_USER_MERCHANT_ASSIGNMENT_HELPER,
} from '../portalUserMerchantAccessCopy';
import {
  PORTAL_USER_PASSWORD_RESET_EMAIL_CHECKBOX_LABEL,
  PORTAL_USER_SIGN_UP_EMAIL_CHECKBOX_LABEL,
  PORTAL_USER_SIGN_UP_EMAIL_CHECKBOX_TOOLTIP,
} from '../portalUserEmailActions';
import { useIsSkipifyAdmin } from '../../../hooks';

const SKIPIFY_INTERNAL_ROLE_NAMES = new Set<string>([
  AuthRoles.SKIPIFY_ADMIN,
  AuthRoles.SKIPIFY_VIEWER,
]);

const isSkipifyInternalRoleName = (roleName: string) => SKIPIFY_INTERNAL_ROLE_NAMES.has(roleName);

const CHILD_MERCHANT_ROLE_NAMES = new Set<string>([
  AuthRoles.CHILD_MERCHANT_ADMIN,
  AuthRoles.CHILD_MERCHANT_EDITOR,
]);

const roleNamesIncludeChildMerchant = (roleNames: string[]) =>
  roleNames.some((name) => CHILD_MERCHANT_ROLE_NAMES.has(name));

const SEND_ACCESS_EMAIL_CHECKBOX_ID = 'portal-user-send-access-email';

const isSkipifyEmail = (email: string) => email.trim().toLowerCase().endsWith('@skipify.com');

const roleNamesIncludeSkipifyInternal = (roleNames: string[]) =>
  roleNames.some(isSkipifyInternalRoleName);

type Mode = 'add' | 'edit';

type FormValues = {
  email: string;
  firstName: string;
  lastName: string;
  roleNames: string[];
  merchants: MerchantAccess[];
  sendAccessEmail: boolean;
};

const buildSchema = (mode: Mode, existingEmail?: string) => {
  const base = z.object({
    email:
      mode === 'add'
        ? z.string().trim().email('Enter a valid email address')
        : z.string().optional().default(''),
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    roleNames: z.array(z.string()).min(1, 'Select at least one role'),
    merchants: z.array(z.custom<MerchantAccess>()),
    sendAccessEmail: z.boolean(),
  });

  return base.superRefine((values, ctx) => {
    const emailForRules = mode === 'add' ? values.email : (existingEmail ?? '');
    const usesSkipifyInternalRoles = roleNamesIncludeSkipifyInternal(values.roleNames);

    if (usesSkipifyInternalRoles && !isSkipifyEmail(emailForRules)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['roleNames'],
        message: 'SkipifyAdmin and SkipifyViewer can only be assigned to @skipify.com emails',
      });
    }

    if (!usesSkipifyInternalRoles && values.merchants.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['merchants'],
        message: 'Select at least one merchant',
      });
    }
  });
};

interface Props {
  isOpen: boolean;
  mode: Mode;
  roles: Auth0RoleSummary[];
  user?: PortalUserDetail;
  /** Pre-select merchants when creating a user (e.g. current merchant context). */
  initialMerchants?: MerchantAccess[];
  onClose: () => void;
  onSubmit: (body: CreatePortalUserBody | UpdatePortalUserBody) => Promise<void>;
}

export const PortalUserFormDialog = ({
  isOpen,
  mode,
  roles,
  user,
  initialMerchants,
  onClose,
  onSubmit,
}: Props) => {
  const isSkipifyAdmin = useIsSkipifyAdmin();
  const [isProcessing, setIsProcessing] = useState(false);
  const [serverError, setServerError] = useState<string>();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(buildSchema(mode, user?.email)),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      roleNames: [],
      merchants: [],
      sendAccessEmail: true,
    },
  });

  const watchedEmail = useWatch({ control, name: 'email' });
  const watchedRoleNamesValue = useWatch({ control, name: 'roleNames' });
  const watchedRoleNames = useMemo(() => watchedRoleNamesValue ?? [], [watchedRoleNamesValue]);

  const emailForSkipifyCheck = mode === 'add' ? watchedEmail : (user?.email ?? '');
  const canAssignSkipifyInternalRoles = isSkipifyEmail(emailForSkipifyCheck);
  const usesSkipifyInternalRoles = roleNamesIncludeSkipifyInternal(watchedRoleNames);
  const usesChildMerchantRoles = roleNamesIncludeChildMerchant(watchedRoleNames);
  const merchantAccessDisabled = usesSkipifyInternalRoles;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (mode === 'edit' && user) {
      reset({
        email: user.email,
        firstName: user.givenName ?? '',
        lastName: user.familyName ?? '',
        roleNames: normalizePortalRoleNames(user.roles.map((role) => role.name)),
        merchants: user.merchants,
        sendAccessEmail: false,
      });
      return;
    }

    reset({
      email: '',
      firstName: '',
      lastName: '',
      roleNames: [],
      merchants: initialMerchants ?? [],
      sendAccessEmail: true,
    });
  }, [isOpen, initialMerchants, mode, reset, user]);

  // Skipify-internal roles do not use direct merchant access — clear the selection when enabled.
  useEffect(() => {
    if (merchantAccessDisabled) {
      setValue('merchants', []);
    }
  }, [merchantAccessDisabled, setValue]);

  // Drop Skipify-internal roles if the email is no longer eligible.
  useEffect(() => {
    if (canAssignSkipifyInternalRoles) {
      return;
    }
    if (!roleNamesIncludeSkipifyInternal(watchedRoleNames)) {
      return;
    }
    setValue(
      'roleNames',
      watchedRoleNames.filter((roleName) => !isSkipifyInternalRoleName(roleName)),
    );
  }, [canAssignSkipifyInternalRoles, setValue, watchedRoleNames]);

  const submitForm = async (values: FormValues) => {
    setServerError(undefined);
    setIsProcessing(true);

    const roleNamesForSubmit = normalizePortalRoleNames(values.roleNames);
    const merchantIds = roleNamesIncludeSkipifyInternal(roleNamesForSubmit)
      ? []
      : values.merchants.map((merchant) => merchant.merchantId);

    try {
      if (mode === 'add') {
        await onSubmit({
          email: values.email.trim().toLowerCase(),
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          roleNames: roleNamesForSubmit,
          merchantIds,
          sendAccessEmail: values.sendAccessEmail,
        });
      } else {
        await onSubmit({
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          roleNames: roleNamesForSubmit,
          merchantIds,
          sendAccessEmail: values.sendAccessEmail || undefined,
        });
      }
      onClose();
    } catch (error) {
      setServerError(error instanceof Error ? error.message : 'Something went wrong');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (isProcessing) {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      <DialogCloseButton onClose={handleClose} disabled={isProcessing} />
      <DialogTitle sx={{ fontWeight: 600 }}>
        {mode === 'add' ? 'Add portal user' : 'Edit portal user'}
      </DialogTitle>
      <DialogContent>
        <Stack component="form" spacing={2.5} sx={{ pt: 1 }} onSubmit={handleSubmit(submitForm)}>
          {mode === 'add' ? (
            <TextField
              label="Email"
              required
              disabled={isProcessing}
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
          ) : (
            <TextField
              label="Email"
              value={user?.email ?? ''}
              fullWidth
              disabled
              helperText="Email cannot be changed"
            />
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="First name"
              required
              fullWidth
              disabled={isProcessing}
              {...register('firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
            />
            <TextField
              label="Last name"
              required
              fullWidth
              disabled={isProcessing}
              {...register('lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
            />
          </Stack>
          <Divider />
          <Typography component="h3" fontWeight="semibold">
            Roles
          </Typography>
          <FormGroup
            sx={{
              pl: 0.5,
              gap: 0.75,
              width: 'fit-content',
              maxWidth: '100%',
            }}
          >
            {roles.map((role) => (
              <Controller
                key={role.id}
                name="roleNames"
                control={control}
                render={({ field }) => {
                  const isSkipifyInternal = isSkipifyInternalRoleName(role.name);
                  const skipifyBlocked = isSkipifyInternal && !canAssignSkipifyInternalRoles;
                  const redundancyHelper = getRoleRedundancyHelperText(role.name, field.value);
                  const isBlocked = Boolean(redundancyHelper) || skipifyBlocked;
                  const isDisabled = isBlocked || isProcessing;

                  const roleLabel = getPortalUserRoleLabel(role.name);
                  const checkboxId = `portal-user-role-${role.id}`;
                  const helperText = skipifyBlocked
                    ? 'Only available for @skipify.com emails'
                    : redundancyHelper;

                  return (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', maxWidth: '100%' }}>
                      <Checkbox
                        id={checkboxId}
                        checked={field.value.includes(role.name)}
                        disabled={isDisabled}
                        sx={{ flexShrink: 0, p: 0.75, mt: -0.25 }}
                        onChange={(event) => {
                          if (isDisabled) {
                            return;
                          }
                          if (event.target.checked) {
                            field.onChange(normalizePortalRoleNames([...field.value, role.name]));
                          } else {
                            field.onChange(field.value.filter((name) => name !== role.name));
                          }
                        }}
                      />
                      <Box sx={{ minWidth: 0, pt: 0.5 }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            columnGap: 0.5,
                            rowGap: 0,
                          }}
                        >
                          <Typography
                            component="label"
                            htmlFor={checkboxId}
                            sx={{
                              fontSize: 14,
                              lineHeight: 1.43,
                              color: isDisabled ? 'text.disabled' : 'text.primary',
                              cursor: isDisabled ? 'default' : 'pointer',
                            }}
                          >
                            {roleLabel}
                          </Typography>
                          {role.description ? (
                            <InfoTooltip
                              title={role.description}
                              ariaLabel={`${roleLabel} description`}
                            />
                          ) : null}
                        </Box>
                        {helperText ? (
                          <Typography
                            component="p"
                            sx={{
                              fontSize: 12,
                              lineHeight: 1.35,
                              color: 'text.secondary',
                              m: 0,
                              mt: 0.25,
                            }}
                          >
                            {helperText}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  );
                }}
              />
            ))}
          </FormGroup>
          {errors.roleNames && <FormHelperText error>{errors.roleNames.message}</FormHelperText>}
          <Typography component="h3" fontWeight="semibold">
            Merchant access
          </Typography>
          {merchantAccessDisabled && isSkipifyAdmin ? (
            <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: -1 }}>
              SkipifyAdmin and SkipifyViewer do not use direct merchant access.
            </Typography>
          ) : !merchantAccessDisabled ? (
            <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: -1 }}>
              {usesChildMerchantRoles
                ? PORTAL_USER_CHILD_MERCHANT_MERCHANT_ASSIGNMENT_HELPER
                : PORTAL_USER_MERCHANT_ASSIGNMENT_HELPER}
            </Typography>
          ) : null}
          <Controller
            name="merchants"
            control={control}
            render={({ field }) => (
              <MerchantAccessMultiSelect
                value={field.value}
                onChange={field.onChange}
                label="Search merchants"
                disabled={merchantAccessDisabled || isProcessing}
                showSkipifyInternalMerchantHint={isSkipifyAdmin}
              />
            )}
          />
          {errors.merchants && <FormHelperText error>{errors.merchants.message}</FormHelperText>}
          <Controller
            name="sendAccessEmail"
            control={control}
            render={({ field }) => {
              const isPasswordResetOption = mode === 'edit' && user?.hasLoggedIn;
              const accessEmailLabel = isPasswordResetOption
                ? PORTAL_USER_PASSWORD_RESET_EMAIL_CHECKBOX_LABEL
                : PORTAL_USER_SIGN_UP_EMAIL_CHECKBOX_LABEL;

              return (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', maxWidth: '100%' }}>
                  <Checkbox
                    id={SEND_ACCESS_EMAIL_CHECKBOX_ID}
                    checked={field.value}
                    disabled={isProcessing}
                    sx={{ flexShrink: 0, p: 0.75, mt: -0.25 }}
                    onChange={field.onChange}
                  />
                  <Box sx={{ minWidth: 0, pt: 0.5 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        columnGap: 0.5,
                        rowGap: 0,
                      }}
                    >
                      <Typography
                        component="label"
                        htmlFor={SEND_ACCESS_EMAIL_CHECKBOX_ID}
                        sx={{
                          fontSize: 14,
                          lineHeight: 1.43,
                          color: isProcessing ? 'text.disabled' : 'text.primary',
                          cursor: isProcessing ? 'default' : 'pointer',
                        }}
                      >
                        {accessEmailLabel}
                      </Typography>
                      {!isPasswordResetOption ? (
                        <InfoTooltip
                          title={PORTAL_USER_SIGN_UP_EMAIL_CHECKBOX_TOOLTIP}
                          ariaLabel="Send sign-up email details"
                        />
                      ) : null}
                    </Box>
                  </Box>
                </Box>
              );
            }}
          />
          {serverError && <FormHelperText error>{serverError}</FormHelperText>}
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button text="Cancel" theme="secondary" onClick={handleClose} disabled={isProcessing} />
            <Button
              text={mode === 'add' ? 'Add user' : 'Save changes'}
              type="submit"
              loading={isProcessing}
            />
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
